import { lazy, Suspense, type ComponentType } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import App from '../App';
import HomeRoute from './home';
import NotFoundRoute from './not-found';
import { discoveredModules, type ModuleRoute, type ModuleChildRoute } from './modules';

/**
 * Construit la config React Router à partir des modules auto-découverts.
 * Voir routes/modules.ts pour la convention de discovery.
 */

export { discoveredModules };
export type { ModuleRoute, ModuleChildRoute };

function toRouteObject(m: ModuleRoute): RouteObject {
  const children: RouteObject[] = (m.children ?? []).map((c) => ({
    path: c.path,
    element: <Suspense fallback={<RouteFallback />}>{c.element}</Suspense>,
  }));
  return {
    path: m.path,
    element: <Suspense fallback={<RouteFallback />}>{m.element}</Suspense>,
    children: children.length > 0 ? children : undefined,
  };
}

function RouteFallback(): JSX.Element {
  return (
    <div className="p-6 text-sm text-slate-500" role="status" aria-live="polite">
      Chargement du module…
    </div>
  );
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    errorElement: <NotFoundRoute />,
    children: [
      { index: true, element: <HomeRoute /> },
      ...discoveredModules.map(toRouteObject),
      { path: '*', element: <NotFoundRoute /> },
    ],
  },
]);

/**
 * Helper exporté aux modules : wrappe le composant en lazy + retourne un ModuleRoute prêt.
 *
 * Exemple côté module (modules/herbo-commandes/web/route.tsx) :
 *   import { createModuleRoute } from '@/routes';
 *   export default createModuleRoute({
 *     path: 'commandes', label: 'Commandes', icon: 'shopping-cart',
 *     loader: () => import('./pages/CommandesPage')
 *   });
 */
export function createModuleRoute(opts: {
  readonly path: string;
  readonly label: string;
  readonly icon: string;
  readonly loader: () => Promise<{ default: ComponentType }>;
  readonly children?: ModuleRoute['children'];
}): ModuleRoute {
  const Lazy = lazy(opts.loader);
  return {
    path: opts.path,
    label: opts.label,
    icon: opts.icon,
    element: <Lazy />,
    children: opts.children,
  };
}
